import { Fragment, useEffect, useState, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  Polyline,
  OverlayView,
  InfoWindow,
  TransitLayer,
} from "@react-google-maps/api";
import { toast } from "react-toastify";
import {
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_LOADER_ID,
} from "../../../../src/Pages/DMS/GpsTrackManagement/Googlemapsloader";
import { postDataWithBody } from "../../../Services/Services";
import { fetchVehicleTrackingWithIdealPoints } from "../../../Services/EndPoints";

// ─── Constants ────────────────────────────────────────────────────────────────

const MAP_CONTAINER_STYLE = { width: "100%", height: "100%" };
const DEFAULT_CENTER = { lat: 17.4435, lng: 78.3485 };

// ─── Map type options + layer control (matches GPS Tracking reference) ────────

const MAP_TYPE_OPTIONS = [
  {
    id: "roadmap", label: "Standard",
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/><line x1="9" y1="3" x2="9" y2="18"/><line x1="15" y1="6" x2="15" y2="21"/></svg>,
  },
  {
    id: "satellite", label: "Satellite",
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
  },
  {
    id: "terrain", label: "Terrain",
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 18 9 6 13 13 16 9 21 18"/></svg>,
  },
  {
    id: "hybrid", label: "Hybrid",
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/><line x1="2" y1="12" x2="22" y2="12"/></svg>,
  },
];

const CustomLayerControl = ({ mapType, setMapType }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: "absolute", top: 10, right: 10, zIndex: 1000 }}>
      <button
        onClick={() => setOpen((p) => !p)}
        style={{
          width: 40, height: 40, border: "none", background: "white",
          borderRadius: 8, cursor: "pointer", display: "flex",
          alignItems: "center", justifyContent: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="12 2 2 7 12 12 22 7 12 2"/>
          <polyline points="2 17 12 22 22 17"/>
          <polyline points="2 12 12 17 22 12"/>
        </svg>
      </button>
      {open && (
        <div style={{
          position: "absolute", top: 45, right: 0,
          background: "white", borderRadius: 10, padding: 8,
          boxShadow: "0 4px 16px rgba(0,0,0,0.14)", minWidth: 160,
          border: "1px solid #e2e8f0",
        }}>
          {MAP_TYPE_OPTIONS.map((opt) => (
            <div
              key={opt.id}
              onClick={() => { setMapType(opt.id); setOpen(false); }}
              style={{
                display: "flex", alignItems: "center", gap: 9,
                padding: "8px 10px", cursor: "pointer", borderRadius: 7,
                background: mapType === opt.id ? "#f0fdfb" : "transparent",
                transition: "background 0.15s",
              }}
            >
              <span style={{ color: mapType === opt.id ? "#1a7a73" : "#94a3b8" }}>{opt.icon}</span>
              <span style={{
                fontSize: 13,
                fontWeight: mapType === opt.id ? 600 : 400,
                color: mapType === opt.id ? "#1a7a73" : "#334155",
              }}>{opt.label}</span>
              {mapType === opt.id && (
                <span style={{ marginLeft: "auto", width: 7, height: 7, borderRadius: "50%", background: "#1a7a73" }} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Loading overlay ──────────────────────────────────────────────────────────

const LoadingOverlay = ({ message = "Loading..." }) => (
  <div style={{
    position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
    minHeight: "100vh",
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center",
    zIndex: 999, borderRadius: 0, overflow: "hidden",
  }}>
    <div className="spinner-border text-primary mb-3" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
    <div style={{ fontSize: "14px", color: "#666", fontWeight: "500" }}>
      {message}
    </div>
  </div>
);

// ─── Idle marker popup ────────────────────────────────────────────────────────

const IdlePopupContent = ({ idlePeriod, index, onClose, formatDuration, formatTrackingTime }) => (
  <div
    onClick={(e) => e.stopPropagation()}
    style={{
      transform: "translate(-50%, calc(-100% - 20px))",
      minWidth: 280,
      background: "#fff",
      borderRadius: 8,
      boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
      overflow: "visible",
      fontFamily: "sans-serif",
      fontSize: 13,
      animation: "fadeInPanel 0.15s ease",
      pointerEvents: "auto",
      position: "relative",
    }}
  >
    <div style={{
      position: "absolute", bottom: -10, left: "50%",
      transform: "translateX(-50%)", width: 0, height: 0,
      borderLeft: "10px solid transparent",
      borderRight: "10px solid transparent",
      borderTop: "10px solid #ff4444",
    }} />

    <div style={{
      color: "#ff4444", fontSize: 16,
      borderBottom: "2px solid #ff4444", paddingBottom: 4,
      padding: "12px 14px 8px",
      display: "flex", justifyContent: "space-between", alignItems: "flex-start",
    }}>
      <strong>🚗 Idle Location #{index + 1}</strong>
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "#999", lineHeight: 1, padding: 0 }}
      >✕</button>
    </div>

    <div style={{ fontSize: 13, lineHeight: 1.5, padding: "0 14px 12px" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
        <div>
          <strong style={{ color: "#666" }}>Duration:</strong>
          <span style={{ color: "#ff6600", fontWeight: "bold" }}>
            {" "}{formatDuration(idlePeriod.duration)}
          </span>
        </div>
      </div>

      <div style={{ marginBottom: 8 }}>
        <strong style={{ color: "#666" }}>Time Period:</strong>
        <div style={{ fontSize: 11, color: "#555" }}>
          <strong>From:</strong> {formatTrackingTime(idlePeriod.startTime)}<br />
          <strong>To:</strong>   {formatTrackingTime(idlePeriod.endTime)}
        </div>
      </div>

      <div style={{
        backgroundColor: "#f8f9fa", padding: 6, borderRadius: 4,
        border: "1px solid #dee2e6", marginTop: 8,
      }}>
        <strong style={{ color: "#666", fontSize: 11 }}>Location:</strong><br />
        <span style={{ fontSize: 10, color: "#666", fontFamily: "monospace" }}>
          {idlePeriod.address || `${idlePeriod.location.lat}, ${idlePeriod.location.lng}`}
        </span>
      </div>

      <div style={{
        marginTop: 8, padding: 4,
        backgroundColor: idlePeriod.duration > 30 ? "#fee2e2" : "#fef3cd",
        borderRadius: 3, fontSize: 11, textAlign: "center",
      }}>
        {idlePeriod.duration > 30 ? "⚠️ Long idle period detected" : "ℹ️ Short stop detected"}
      </div>
    </div>
  </div>
);

// ─── Start / End popup ────────────────────────────────────────────────────────

const EndpointPopupContent = ({ type, vehicleTime, address, coords, onClose, formatTrackingTime }) => {
  const isStart     = type === "start";
  const headerColor = isStart ? "#22c55e" : "#ef4444";

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        transform: "translate(-50%, calc(-100% - 48px))",
        minWidth: 250,
        background: "#fff",
        borderRadius: 8,
        boxShadow: "0 4px 16px rgba(0,0,0,0.22)",
        overflow: "visible",
        fontFamily: "sans-serif",
        fontSize: 12,
        animation: "fadeInPanel 0.15s ease",
        pointerEvents: "auto",
        position: "relative",
      }}
    >
      <div style={{
        position: "absolute", bottom: -10, left: "50%",
        transform: "translateX(-50%)", width: 0, height: 0,
        borderLeft: "10px solid transparent",
        borderRight: "10px solid transparent",
        borderTop: `10px solid ${headerColor}`,
      }} />

      <div style={{ padding: "10px 14px 6px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h6 style={{ margin: 0, color: "#ffa100" }}>{isStart ? "Start Point" : "End Point"}</h6>
        <button
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "#999", lineHeight: 1, padding: 0 }}
        >✕</button>
      </div>

      <div style={{ padding: "0 14px 12px", lineHeight: 1.4 }}>
        {vehicleTime && (
          <>
            <strong>{isStart ? "Start Time" : "End Time"}:</strong>{" "}
            {formatTrackingTime(vehicleTime)}<br />
          </>
        )}
        <strong>Address:</strong>
        <div style={{ fontSize: 11, color: "#666", marginTop: 4, maxWidth: 220, wordWrap: "break-word" }}>
          {address || `${coords[0].toFixed(6)}, ${coords[1].toFixed(6)}`}
        </div>
      </div>
    </div>
  );
};

// ─── GPS data-point popup ─────────────────────────────────────────────────────

const DataPointPopupContent = ({ point, index, address, onClose, formatTrackingTime }) => (
  <div
    onClick={(e) => e.stopPropagation()}
    style={{
      transform: "translate(-50%, calc(-100% - 18px))",
      minWidth: 230,
      background: "#fff",
      borderRadius: 8,
      boxShadow: "0 4px 16px rgba(0,0,0,0.22)",
      overflow: "visible",
      fontFamily: "sans-serif",
      fontSize: 12,
      animation: "fadeInPanel 0.15s ease",
      pointerEvents: "auto",
      position: "relative",
    }}
  >
    {/* Caret */}
    <div style={{
      position: "absolute", bottom: -9, left: "50%",
      transform: "translateX(-50%)", width: 0, height: 0,
      borderLeft: "9px solid transparent",
      borderRight: "9px solid transparent",
      borderTop: "9px solid #4444ff",
    }} />

    {/* Header */}
    <div style={{
      background: "#4444ff", color: "#fff",
      padding: "8px 12px",
      borderRadius: "8px 8px 0 0",
      display: "flex", justifyContent: "space-between", alignItems: "center",
    }}>
      <strong style={{ fontSize: 12 }}>📍 GPS Point #{index + 1}</strong>
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        style={{ background: "none", border: "none", cursor: "pointer", fontSize: 15, color: "#fff", lineHeight: 1, padding: 0 }}
      >✕</button>
    </div>

    {/* Body */}
    <div style={{ padding: "10px 12px 12px", lineHeight: 1.6 }}>
      <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 8, rowGap: 4 }}>
        <span style={{ color: "#888", fontWeight: 600 }}>Location:</span>
        <span style={{ color: address ? "#0f172a" : "#999", fontStyle: address ? "normal" : "italic" }}>
          {address || "Fetching address…"}
        </span>
        <span style={{ color: "#888", fontWeight: 600 }}>Time:</span>
        <span style={{ color: "#0f172a" }}>
          {formatTrackingTime(point.vehicle_tracking_time)}
        </span>
        {point.speed != null && (
          <>
            <span style={{ color: "#888", fontWeight: 600 }}>Speed:</span>
            <span style={{ color: "#0f172a" }}>{parseFloat(point.speed).toFixed(1)} km/h</span>
          </>
        )}
      </div>
    </div>
  </div>
);

// ─── SVG marker icon helpers — identical to RouteList ────────────────────────

/**
 * Same teardrop-pin SVG used in RouteList.
 * Must be called only after window.google is available (map loaded).
 */
const makePinSvg = (fillColor) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="44" viewBox="0 0 28 44">
    <path d="M14 0C6.268 0 0 6.268 0 14c0 5.817 3.412 10.83 8.35 13.269L14 44l5.65-16.731C24.588 24.83 28 19.817 28 14 28 6.268 21.732 0 14 0z"
      fill="${fillColor}" stroke="white" stroke-width="1.5"/>
    <circle cx="14" cy="14" r="5.5" fill="white" fill-opacity="0.85"/>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

/** color: "green" → #34A853  |  "red" → #EA4335  |  "blue" → #4285F4 */
const buildPinIcon = (color) => {
  const colorMap = { green: "#34A853", red: "#EA4335", blue: "#4285F4" };
  return {
    url:        makePinSvg(colorMap[color] || colorMap.blue),
    scaledSize: new window.google.maps.Size(28, 44),
    anchor:     new window.google.maps.Point(14, 44),
  };
};

const buildDotIcon = () => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12">
    <circle cx="6" cy="6" r="4" fill="#4444ff" stroke="white" stroke-width="2"/>
  </svg>`;
  return {
    url:        `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(12, 12),
    anchor:     new window.google.maps.Point(6, 6),
  };
};

const buildIdleIcon = (idlePeriod, index) => {
  const intensity  = Math.min(idlePeriod.duration / 60, 1);
  const green      = Math.round(140 * (1 - intensity));
  const mainColor  = `rgb(255,${green},0)`;
  const mainR = 18, mainCx = 18, mainCy = 18;
  const badgeR = 11, badgeCx = 30, badgeCy = 8;
  const canvasW    = badgeCx + badgeR + 2;
  const canvasH    = mainCy + mainR + 2;
  const countText  = idlePeriod.pointsCount || "";
  const countFSize = countText.toString().length > 2 ? 7 : 9;

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${canvasW}" height="${canvasH}" viewBox="0 0 ${canvasW} ${canvasH}">
    <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.35)"/>
    </filter>
    <circle cx="${mainCx}" cy="${mainCy}" r="${mainR}"
            fill="${mainColor}" stroke="white" stroke-width="2.5" filter="url(#shadow)"/>
    <text x="${mainCx}" y="${mainCy + 5}" text-anchor="middle" dominant-baseline="middle"
          font-size="13" font-weight="bold" fill="white" font-family="Arial,sans-serif">${index + 1}</text>
    <circle cx="${badgeCx}" cy="${badgeCy}" r="${badgeR}" fill="#e53e3e" stroke="white" stroke-width="2"/>
    <text x="${badgeCx}" y="${badgeCy + 1}" text-anchor="middle" dominant-baseline="middle"
          font-size="${countFSize}" font-weight="bold" fill="white" font-family="Arial,sans-serif">${countText}</text>
  </svg>`;

  return {
    url:        `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(canvasW, canvasH),
    anchor:     new window.google.maps.Point(mainCx, mainCy),
  };
};

// ─── Distance helper ──────────────────────────────────────────────────────────

const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R    = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a    =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// ─── Vehicle animation helpers ────────────────────────────────────────────────

const _calcBearing = (from, to) => {
  const toRad = d => d * Math.PI / 180;
  const lat1 = toRad(from.lat), lat2 = toRad(to.lat);
  const dLng = toRad(to.lng - from.lng);
  return (Math.atan2(Math.sin(dLng) * Math.cos(lat2), Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng)) * 180 / Math.PI + 360) % 360;
};

const TRACK_VEHICLE_EMOJI = {
  car: '🚗', sedan: '🚗', bus: '🚌', minibus: '🚌',
  truck: '🚛', lorry: '🚛', bike: '🏍️', bicycle: '🚲',
  ambulance: '🚑', firetruck: '🚒', police: '🚓', van: '🚐',
};

const getTrackVehicleIcon = (vehicleType, heading = 0) => {
  const type  = (vehicleType || '').toLowerCase().replace(/[\s_-]+/g, '');
  const emoji = TRACK_VEHICLE_EMOJI[type] || '🚘';
  const deg   = Math.round(heading);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="52" height="52" viewBox="0 0 52 52">
  <circle cx="26" cy="26" r="24" fill="#0f4a45" fill-opacity="0.92" stroke="#fff" stroke-width="2.5"/>
  <text x="26" y="32" text-anchor="middle" font-size="22">${emoji}</text>
  <g transform="translate(26,26) rotate(${deg})">
    <polygon points="0,-22 4,-13 0,-16 -4,-13" fill="#fff" opacity="0.95"/>
  </g>
</svg>`;
  const url = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  return { url, scaledSize: { width: 52, height: 52 }, anchor: { x: 26, y: 26 } };
};

// ─── Main component ───────────────────────────────────────────────────────────

const GpsTrackViewDetails = () => {
  const location = useLocation();
  const { data, vehicleType: _vehicleType, vehicleData: vehicleInfo } = location.state || {};

  const [vehicleTrackingData,   setVehicleTrackingData]   = useState([]);
  const [routeCoordinates,      setRouteCoordinates]      = useState([]);
  const [isLoadingData,         setIsLoadingData]         = useState(false);
  const [startAddress,          setStartAddress]          = useState("");
  const [endAddress,            setEndAddress]            = useState("");
  const [apiError,              setApiError]              = useState("");
  const [showDataPoints,        setShowDataPoints]        = useState(false);
  const [roadSnappingEnabled,   setRoadSnappingEnabled]   = useState(false);
  const [mapType,               setMapType]               = useState("roadmap");
  const [idlePeriods,           setIdlePeriods]           = useState([]);
  const [totalIdleTime,         setTotalIdleTime]         = useState({ totalMinutes: 0, formattedTotal: "0 minutes", idleCount: 0 });
  const [isLoadingMap,          setIsLoadingMap]          = useState(false);
  const [isLoadingPolylines,    setIsLoadingPolylines]    = useState(false);
  const [idleCalculationPoints, setIdleCalculationPoints] = useState([]);
  const [activeIdlePopup,       setActiveIdlePopup]       = useState(null);
  const [activeEndpointPopup,   setActiveEndpointPopup]   = useState(null);
  const [activeDataPointPopup,  setActiveDataPointPopup]  = useState(null);
  const [datapointAddr,         setDatapointAddr]         = useState({ index: null, address: null, loading: false });
  const [isFullscreen,          setIsFullscreen]          = useState(false);
  const [searchQuery,           setSearchQuery]           = useState("");
  const [searchResults,         setSearchResults]         = useState([]);
  const [isSearching,           setIsSearching]           = useState(false);
  const [showTransit,           setShowTransit]           = useState(false);

  // ── Playback animation state ───────────────────────────────────────────────
  const [animPlaying,      setAnimPlaying]      = useState(false);
  const [animPos,          setAnimPos]          = useState(null);
  const [animHeading,      setAnimHeading]      = useState(0);
  const [showVehiclePopup, setShowVehiclePopup] = useState(false);
  const [animSpeed,        setAnimSpeed]        = useState(1);

  const animRef      = useRef(null);
  const animIdxRef   = useRef(0);
  const animStartRef = useRef(null);

  const mapRef            = useRef(null);
  const geocoderRef       = useRef(null);
  const autocompleteRef   = useRef(null);
  const searchTimerRef    = useRef(null);
  const mapContainerRef   = useRef(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id:               GOOGLE_MAPS_LOADER_ID,
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries:        GOOGLE_MAPS_LIBRARIES,
  });

  // ── fitBounds ─────────────────────────────────────────────────────────────

  const fitBoundsToData = useCallback(() => {
    if (!mapRef.current || !isLoaded) return;
    const allPoints = [
      ...vehicleTrackingData.map((p) => ({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) })),
      ...routeCoordinates.map(([lat, lng]) => ({ lat, lng })),
    ].filter((c) => !isNaN(c.lat) && !isNaN(c.lng));
    if (allPoints.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    allPoints.forEach((c) => bounds.extend(c));
    mapRef.current.fitBounds(bounds, { top: 20, right: 20, bottom: 20, left: 20 });
  }, [vehicleTrackingData, routeCoordinates, isLoaded]);

  useEffect(() => { fitBoundsToData(); }, [fitBoundsToData]);

  // ── Fullscreen toggle ─────────────────────────────────────────────────────

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = mapContainerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen().catch((err) => console.warn("Fullscreen error:", err));
    } else {
      document.exitFullscreen();
    }
  }, []);

  // ── Location search ───────────────────────────────────────────────────────

  const searchLocation = useCallback((query) => {
    if (!query.trim() || !autocompleteRef.current) { setSearchResults([]); return; }
    setIsSearching(true);
    autocompleteRef.current.getPlacePredictions(
      { input: query, componentRestrictions: { country: "in" } },
      (predictions, status) => {
        setIsSearching(false);
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSearchResults(predictions);
        } else {
          setSearchResults([]);
        }
      }
    );
  }, []);

  const selectSearchResult = useCallback((place) => {
    setSearchQuery(place.description);
    setSearchResults([]);
    if (!geocoderRef.current) return;
    geocoderRef.current.geocode({ placeId: place.place_id }, (results, status) => {
      if (status === "OK" && results[0]) {
        const loc = results[0].geometry.location;
        const viewport = results[0].geometry.viewport;
        if (mapRef.current) {
          if (viewport) {
            mapRef.current.fitBounds(viewport);
          } else {
            mapRef.current.setCenter({ lat: loc.lat(), lng: loc.lng() });
            mapRef.current.setZoom(15);
          }
        }
      }
    });
  }, []);

  const handleSearchInputChange = useCallback((e) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => searchLocation(val), 500);
  }, [searchLocation]);

  const clearSearch = useCallback(() => {
    setSearchQuery("");
    setSearchResults([]);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
  }, []);

  // ── onMapLoad — initialise Geocoder here so window.google is ready ─────────

  const onMapLoad = useCallback((map) => {
    mapRef.current        = map;
    geocoderRef.current   = new window.google.maps.Geocoder();
    autocompleteRef.current = new window.google.maps.places.AutocompleteService();
    map.addListener("click", () => {
      setActiveIdlePopup(null);
      setActiveEndpointPopup(null);
      setActiveDataPointPopup(null);
    });
  }, []);

  // ── Format helpers ────────────────────────────────────────────────────────

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${Math.round(minutes)} minutes`;
    const hours            = Math.floor(minutes / 60);
    const remainingMinutes = Math.round(minutes % 60);
    return `${hours}h ${remainingMinutes}m`;
  };

  const formatTrackingTime = (timeString) => {
    const date  = new Date(timeString);
    const day   = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year  = date.getFullYear();
    let hours   = date.getHours();
    const ampm  = hours >= 12 ? "PM" : "AM";
    hours       = hours % 12 || 12;
    return `${day}-${month}-${year} ${String(hours).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")} ${ampm}`;
  };

  const formatDateTime = (dateTime) => {
    const date = new Date(dateTime);
    const pad  = (n) => String(n).padStart(2, "0");
    const ms   = String(date.getMilliseconds()).padStart(3, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
      `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.${ms}`;
  };

  const getTimeDifferenceInMinutes = (startTime, endTime) => {
    const start = new Date(startTime);
    const end   = new Date(endTime);
    if (end <= start) {
      console.warn("End time is not after start time:", { startTime, endTime });
      return 0;
    }
    const diffMs = Math.abs(end - start);
    console.log(`Time difference: ${startTime} to ${endTime} = ${(diffMs / 60000).toFixed(1)} minutes`);
    return diffMs / (1000 * 60);
  };

  // ── Google Maps Geocoder — replaces Nominatim ─────────────────────────────
  // Mirrors the same component-lookup pattern used in RouteList.

  const getAddressFromLatLng = useCallback((lat, lng) => {
    return new Promise((resolve) => {
      if (!geocoderRef.current) {
        resolve(`${parseFloat(lat).toFixed(6)}, ${parseFloat(lng).toFixed(6)}`);
        return;
      }
      geocoderRef.current.geocode(
        { location: { lat: parseFloat(lat), lng: parseFloat(lng) } },
        (results, status) => {
          if (status === "OK" && results && results[0]) {
            const components = results[0].address_components;
            const get        = (...types) => {
              for (const type of types) {
                const c = components.find((ac) => ac.types.includes(type));
                if (c) return c.long_name;
              }
              return null;
            };
            const road    = get("route", "premise", "establishment");
            const area    = get("sublocality_level_1", "sublocality", "neighborhood");
            const city    = get("locality", "administrative_area_level_3");
            const state   = get("administrative_area_level_1");
            const pincode = get("postal_code");
            const parts   = [road, area, city, state, pincode].filter(Boolean);
            resolve(parts.length ? parts.join(", ") : results[0].formatted_address);
          } else {
            console.warn(`Geocode failed (${status}) for ${lat},${lng}`);
            resolve(`${parseFloat(lat).toFixed(6)}, ${parseFloat(lng).toFixed(6)}`);
          }
        }
      );
    });
  }, []); // geocoderRef is a stable ref

  // ── Fetch address when a GPS data-point popup opens ───────────────────────
  useEffect(() => {
    if (activeDataPointPopup === null) return;
    if (datapointAddr.index === activeDataPointPopup && datapointAddr.address !== null) return;
    const point = vehicleTrackingData[activeDataPointPopup];
    if (!point) return;
    setDatapointAddr({ index: activeDataPointPopup, address: null, loading: true });
    getAddressFromLatLng(parseFloat(point.latitude), parseFloat(point.longitude)).then((addr) => {
      setDatapointAddr({ index: activeDataPointPopup, address: addr, loading: false });
    });
  }, [activeDataPointPopup, vehicleTrackingData, getAddressFromLatLng]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Route playback animation ──────────────────────────────────────────────
  useEffect(() => {
    const coords = vehicleTrackingData
      .map(p => ({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) }))
      .filter(p => !isNaN(p.lat) && !isNaN(p.lng));

    if (!animPlaying || coords.length < 2) {
      cancelAnimationFrame(animRef.current);
      return;
    }

    const resumeIdx = Math.min(animIdxRef.current, coords.length - 2);
    animIdxRef.current   = resumeIdx;
    animStartRef.current = null;
    setAnimPos(coords[resumeIdx]);

    const SEG_MS = 300 / animSpeed;
    const tick = (ts) => {
      const idx = animIdxRef.current;
      if (idx + 1 >= coords.length) { setAnimPlaying(false); return; }
      if (animStartRef.current === null) animStartRef.current = ts;
      const t    = Math.min((ts - animStartRef.current) / SEG_MS, 1);
      const from = coords[idx];
      const to   = coords[idx + 1];
      setAnimPos({ lat: from.lat + (to.lat - from.lat) * t, lng: from.lng + (to.lng - from.lng) * t });
      if (t >= 1) {
        setAnimHeading(_calcBearing(from, to));
        animIdxRef.current   += 1;
        animStartRef.current  = null;
      }
      if (animIdxRef.current + 1 < coords.length) {
        animRef.current = requestAnimationFrame(tick);
      } else {
        setAnimPlaying(false);
      }
    };
    animRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animRef.current);
  }, [animPlaying, animSpeed, vehicleTrackingData]);

  const handlePlayPause = () => {
    const coords = vehicleTrackingData
      .map(p => ({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) }))
      .filter(p => !isNaN(p.lat) && !isNaN(p.lng));

    if (animPlaying) {
      cancelAnimationFrame(animRef.current);
      setAnimPlaying(false);
    } else {
      if (animIdxRef.current >= coords.length - 1) {
        animIdxRef.current = 0;
        setAnimHeading(0);
        setAnimPos(null);
      }
      animStartRef.current = null;
      setAnimPlaying(true);
    }
  };

  // ── Idle time clustering ──────────────────────────────────────────────────

  const calculateIdleTimeWithClustering = useCallback((gpsData, options = {}) => {
    const config = {
      maxIdleRadius:        options.maxIdleRadius        || 50,
      minIdleDuration:      options.minIdleDuration      || 5,
      speedThreshold:       options.speedThreshold       || 2,
      minPointsInCluster:   options.minPointsInCluster   || 5,
      maxTimeBetweenPoints: options.maxTimeBetweenPoints || 10,
      ...options,
    };
    if (gpsData.length < config.minPointsInCluster) return [];
    const validPoints = gpsData
      .map((point, index) => ({
        ...point, originalIndex: index,
        speed:     parseFloat(point.speed || 0),
        lat:       parseFloat(point.latitude),
        lng:       parseFloat(point.longitude),
        timestamp: new Date(point.vehicle_tracking_time),
      }))
      .filter((p) => !isNaN(p.lat) && !isNaN(p.lng) && !isNaN(p.speed) && p.speed <= config.speedThreshold)
      .sort((a, b) => a.timestamp - b.timestamp);
    if (validPoints.length < config.minPointsInCluster) return [];

    const stationarySessions = [];
    let currentSession = [validPoints[0]];
    for (let i = 1; i < validPoints.length; i++) {
      const current  = validPoints[i];
      const previous = validPoints[i - 1];
      const timeGap  = (current.timestamp - previous.timestamp) / (1000 * 60);
      const dist     = calculateDistance(previous.lat, previous.lng, current.lat, current.lng) * 1000;
      if (timeGap <= config.maxTimeBetweenPoints && dist <= config.maxIdleRadius) {
        currentSession.push(current);
      } else {
        if (currentSession.length >= config.minPointsInCluster) stationarySessions.push(currentSession);
        currentSession = [current];
      }
    }
    if (currentSession.length >= config.minPointsInCluster) stationarySessions.push(currentSession);

    return stationarySessions.map((session, sessionIndex) => {
      let bestPoint = session[0]; let maxNeighbors = 0;
      session.forEach((candidate) => {
        const neighbors = session.filter(
          (p) => calculateDistance(candidate.lat, candidate.lng, p.lat, p.lng) * 1000 <= 20
        );
        if (neighbors.length > maxNeighbors) { maxNeighbors = neighbors.length; bestPoint = candidate; }
      });
      const startTime = session[0].vehicle_tracking_time;
      const endTime   = session[session.length - 1].vehicle_tracking_time;
      const duration  = getTimeDifferenceInMinutes(startTime, endTime);
      const avgSpeed  = session.reduce((sum, p) => sum + p.speed, 0) / session.length;
      return {
        id: `halt-${sessionIndex}`, startTime, endTime, duration,
        location:      { lat: bestPoint.lat, lng: bestPoint.lng },
        pointsCount:   session.length,
        startIndex:    session[0].originalIndex,
        endIndex:      session[session.length - 1].originalIndex,
        avgSpeed:      avgSpeed.toFixed(1),
        clusterRadius: 100,
        significance:  session.length / duration,
        timeSpan:      duration,
        validHalt:     duration >= config.minIdleDuration && avgSpeed <= config.speedThreshold,
      };
    })
      .filter((p) => p.validHalt)
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Idle calculation points effect ────────────────────────────────────────

  useEffect(() => {
    if (idleCalculationPoints.length === 0) return;

    const normalised = idleCalculationPoints.map((p) => ({
      ...p,
      location: {
        lat: parseFloat(p.location?.lat ?? p.location?.latitude ?? 0),
        lng: parseFloat(p.location?.lng ?? p.location?.longitude ?? 0),
      },
      duration:     typeof p.duration === "number" ? p.duration : parseFloat(p.duration || 0),
      pointsCount:  p.pointsCount ?? p.count ?? 0,
      significance: p.significance != null
        ? p.significance
        : (p.count && p.duration ? p.count / p.duration : 1),
    })).filter((p) =>
      !isNaN(p.location.lat) && p.location.lat !== 0 &&
      !isNaN(p.location.lng) && p.location.lng !== 0
    );

    console.log("Normalised idle points:", normalised.length, normalised);
    setIdlePeriods(normalised);

    const totalMinutes = normalised.reduce((total, p) => total + p.duration, 0);
    setTotalIdleTime({
      totalMinutes,
      formattedTotal: formatDuration(totalMinutes),
      idleCount:      normalised.length,
    });

    const fetchIdleAddresses = async () => {
      const updated = await Promise.all(
        normalised.map(async (period, i) => {
          try {
            // Stagger to avoid Geocoder OVER_QUERY_LIMIT
            await new Promise((r) => setTimeout(r, i * 300));
            const addr = await getAddressFromLatLng(period.location.lat, period.location.lng);
            return { ...period, address: addr };
          } catch {
            return {
              ...period,
              address: `${period.location.lat.toFixed(6)}, ${period.location.lng.toFixed(6)}`,
            };
          }
        })
      );
      setIdlePeriods(updated);
    };

    fetchIdleAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idleCalculationPoints]);

  // ── Start / end addresses ─────────────────────────────────────────────────

  useEffect(() => {
    if (vehicleTrackingData.length > 0) {
      const fetchAddresses = async () => {
        const start = vehicleTrackingData[0];
        const end   = vehicleTrackingData[vehicleTrackingData.length - 1];
        setStartAddress(await getAddressFromLatLng(parseFloat(start.latitude), parseFloat(start.longitude)));
        setEndAddress(  await getAddressFromLatLng(parseFloat(end.latitude),   parseFloat(end.longitude)));
      };
      fetchAddresses();
    }
  }, [vehicleTrackingData, getAddressFromLatLng]);

  // ── GPS helpers ───────────────────────────────────────────────────────────

  const filterGPSPoints = (points, minDistance = 0.002) => {
    if (points.length <= 2) return points;
    const filtered = [points[0]];
    for (let i = 1; i < points.length - 1; i++) {
      const last = filtered[filtered.length - 1];
      const dist = calculateDistance(
        parseFloat(last.lat), parseFloat(last.lng),
        parseFloat(points[i].lat), parseFloat(points[i].lng)
      );
      if (dist >= minDistance) filtered.push(points[i]);
    }
    filtered.push(points[points.length - 1]);
    console.log(`Filtered GPS points: ${points.length} → ${filtered.length}`);
    return filtered;
  };

  const validateAndFilterGPSPoints = (points, options = {}) => {
    const config = {
      maxSpeed:         options.maxSpeed         || 150,
      maxDistanceJump:  options.maxDistanceJump  || 2,
      minAccuracy:      options.minAccuracy      || 50,
      outlierThreshold: options.outlierThreshold || 3,
      ...options,
    };
    if (points.length <= 2) return points;
    const validatedPoints = [points[0]];
    // referencePoint tracks position for comparison independently of validatedPoints.
    // When a GPS jump is filtered we still advance the reference to the jumped point so
    // the next real point is compared against the new position, not the pre-jump one.
    // Without this, one GPS jump silences 6-10 legitimate points that follow it.
    let referencePoint = points[0];

    for (let i = 1; i < points.length - 1; i++) {
      const current  = points[i];
      const previous = referencePoint;
      const dist     = calculateDistance(
        parseFloat(previous.latitude), parseFloat(previous.longitude),
        parseFloat(current.latitude),  parseFloat(current.longitude)
      );
      const timeDiff = Math.abs(
        new Date(current.vehicle_tracking_time) - new Date(previous.vehicle_tracking_time)
      ) / (1000 * 60 * 60);
      if (timeDiff < 0.001) { console.warn(`Skipping point with minimal time diff at index ${i}`); continue; }
      const impliedSpeed = dist / timeDiff;
      if (dist <= config.maxDistanceJump && impliedSpeed <= config.maxSpeed) {
        validatedPoints.push(current);
        referencePoint = current;
      } else {
        // Advance the reference even on a filtered point so the algorithm
        // recovers within 1-2 readings instead of cascading for 6-10 points.
        referencePoint = current;
        console.warn(`Filtered outlier at index ${i}: dist=${dist.toFixed(3)}km, speed=${impliedSpeed.toFixed(1)}km/h`);
      }
    }
    if (points.length > 1) validatedPoints.push(points[points.length - 1]);
    console.log(`Speed/distance filtering: ${points.length} → ${validatedPoints.length} points`);
    return validatedPoints;
  };

  function interpolateRoute(points) {
    if (points.length < 2) return points.map((p) => [p.lat, p.lng]);
    const route = [];
    for (let i = 0; i < points.length - 1; i++) {
      const start = points[i]; const end = points[i + 1];
      route.push([start.lat, start.lng]);
      for (let j = 1; j < 5; j++) {
        const r = j / 5;
        route.push([start.lat + (end.lat - start.lat) * r, start.lng + (end.lng - start.lng) * r]);
      }
    }
    route.push([points[points.length - 1].lat, points[points.length - 1].lng]);
    return route;
  }

  // ── Google Directions API — route following ───────────────────────────────
  // Unlike snapToRoads (which only snaps individual points to the nearest road
  // segment), DirectionsService actually ROUTES between waypoints along the road
  // network, producing a polyline that correctly follows every curve and turn.
  //
  // Limit: origin + 23 intermediate waypoints + destination = 25 total.
  // We pick evenly-spaced representative GPS points within that budget.

  // Roads API snapToRoads — snaps each GPS point to the road the vehicle was
  // actually on, respecting travel direction. Falls back to DirectionsService
  // segments if the Roads API is not enabled on the key.
  const snapPointsToRoad = async (points) => {
    const valid = points.filter(p => {
      const la = parseFloat(p.lat), lo = parseFloat(p.lng);
      return !isNaN(la) && !isNaN(lo) && la >= -90 && la <= 90 && lo >= -180 && lo <= 180;
    });
    if (valid.length < 2) return points.map(p => [p.lat || 0, p.lng || 0]);

    // ── Pre-processing: speed-aware noise reduction ───────────────────────────
    const toRad = d => d * Math.PI / 180;
    const haversineM = (a, b) => {
      const dLat = toRad(parseFloat(b.lat) - parseFloat(a.lat));
      const dLng = toRad(parseFloat(b.lng) - parseFloat(a.lng));
      const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(parseFloat(a.lat))) * Math.cos(toRad(parseFloat(b.lat))) * Math.sin(dLng / 2) ** 2;
      return 2 * 6371000 * Math.asin(Math.sqrt(Math.min(1, h)));
    };

    // 1. Speed-aware deduplication:
    //    Moving (speed > 2 km/h) → always keep, no matter how close — Roads API needs
    //    every direction hint to pick the right road in urban areas.
    //    Stationary (speed ≈ 0) → keep only if > 5 m from last kept point to avoid
    //    flooding the batch with GPS chatter from long traffic stops.
    const deduped = [valid[0]];
    for (let i = 1; i < valid.length; i++) {
      const curr        = valid[i];
      const isStationary = (parseFloat(curr.speed) || 0) < 2;
      if (!isStationary || haversineM(deduped[deduped.length - 1], curr) >= 5) {
        deduped.push(curr);
      }
    }
    if (deduped[deduped.length - 1] !== valid[valid.length - 1]) deduped.push(valid[valid.length - 1]);

    // 2. Remove isolated GPS teleport jumps (> 500 m from BOTH neighbours)
    const cleaned = deduped.filter((p, i) => {
      if (i === 0 || i === deduped.length - 1) return true;
      const dPrev = haversineM(deduped[i - 1], p);
      const dNext = haversineM(p, deduped[i + 1]);
      if (dPrev > 500 && dNext > 500) {
        console.warn(`GPS outlier skipped: ${dPrev.toFixed(0)} m prev / ${dNext.toFixed(0)} m next`);
        return false;
      }
      return true;
    });

    let pts = cleaned.length >= 2 ? cleaned : valid;
    console.log(`GPS pre-process: ${valid.length} raw → ${deduped.length} deduped → ${pts.length} clean pts`);

    // ── Gap densification ─────────────────────────────────────────────────────
    // Roads API interpolation breaks down when consecutive GPS points are > 300 m
    // apart (sparse highway / tunnel / signal loss). For those specific gaps, use
    // Directions API — which follows road geometry between any two points — to
    // insert intermediate road points before snapping. Rest of the code unchanged.
    const GAP_M = 300;
    if (pts.some((p, i) => i > 0 && haversineM(pts[i - 1], p) > GAP_M) && window.google?.maps?.DirectionsService) {
      const densified = [pts[0]];
      for (let i = 1; i < pts.length; i++) {
        const gap = haversineM(pts[i - 1], pts[i]);
        if (gap > GAP_M) {
          if (densified.length > 1) await new Promise(r => setTimeout(r, 100));
          const a = pts[i - 1], b = pts[i];
          const midPts = await new Promise(resolve => {
            new window.google.maps.DirectionsService().route(
              {
                origin:      new window.google.maps.LatLng(parseFloat(a.lat), parseFloat(a.lng)),
                destination: new window.google.maps.LatLng(parseFloat(b.lat), parseFloat(b.lng)),
                travelMode:  window.google.maps.TravelMode.DRIVING,
              },
              (result, status) => {
                if (status === window.google.maps.DirectionsStatus.OK && result.routes[0]) {
                  const path = [];
                  result.routes[0].legs.forEach(leg =>
                    leg.steps.forEach(step =>
                      step.path.forEach(pt =>
                        path.push({ lat: pt.lat(), lng: pt.lng(), speed: ((parseFloat(a.speed) || 0) + (parseFloat(b.speed) || 0)) / 2 })
                      )
                    )
                  );
                  resolve(path.slice(1, -1)); // exclude endpoints — already in densified
                } else {
                  resolve([]);
                }
              }
            );
          });
          console.log(`Gap fill: ${gap.toFixed(0)} m gap → ${midPts.length} road pts inserted`);
          densified.push(...midPts);
        }
        densified.push(pts[i]);
      }
      console.log(`Gap densification: ${pts.length} → ${densified.length} pts`);
      pts = densified.length >= 2 ? densified : pts;
    }

    // ── Turn detection ────────────────────────────────────────────────────────
    // Smoothed bearing uses a ±2 point window to avoid GPS noise at low speed.
    // A real turn = bearing shift > 45° where the vehicle is moving (> 5 km/h).
    const calcBearing = (a, b) => {
      const la1 = toRad(parseFloat(a.lat)), la2 = toRad(parseFloat(b.lat));
      const dLo  = toRad(parseFloat(b.lng) - parseFloat(a.lng));
      return (Math.atan2(Math.sin(dLo) * Math.cos(la2), Math.cos(la1) * Math.sin(la2) - Math.sin(la1) * Math.cos(la2) * Math.cos(dLo)) * 180 / Math.PI + 360) % 360;
    };
    const angleDiff    = (b1, b2) => { const d = Math.abs(b1 - b2) % 360; return d > 180 ? 360 - d : d; };
    const smoothBearing = i => calcBearing(pts[Math.max(0, i - 2)], pts[Math.min(pts.length - 1, i + 2)]);

    const turnSet = new Set([0, pts.length - 1]);
    for (let i = 1; i < pts.length - 1; i++) {
      if ((parseFloat(pts[i].speed) || 0) < 5) continue;
      if (angleDiff(smoothBearing(i - 1), smoothBearing(i + 1)) > 45) turnSet.add(i);
    }
    console.log(`Turn detection: ${turnSet.size - 2} turns in ${pts.length} pts`);

    // ── Turn-aware batching ────────────────────────────────────────────────────
    // End each batch at a detected turn so the Roads API always starts a new
    // batch from a clean anchor on the correct road branch — preventing the API
    // from interpolating across a fork or taking the wrong parallel road.
    // Falls back to a fixed-size cut (with 5-pt overlap) when no turn is found.
    const MAX_B  = 93;
    const SZ_OVL = 5;
    const batches = [];
    let bStart = 0;

    while (bStart < pts.length - 1) {
      const limit = bStart + MAX_B;
      let bEnd    = pts.length - 1;
      let turnCut = true;

      if (limit < pts.length - 1) {
        let found = false;
        for (let j = Math.min(limit, pts.length - 2); j > bStart + Math.floor(MAX_B / 2); j--) {
          if (turnSet.has(j)) { bEnd = j; found = true; break; }
        }
        if (!found) { bEnd = limit; turnCut = false; }
      }

      batches.push({ slice: pts.slice(bStart, bEnd + 1), turnCut });
      bStart = turnCut ? bEnd : Math.max(bEnd - SZ_OVL + 1, bStart + 1);
    }
    console.log(`Batches: ${batches.length} (turn-anchored)`);

    // ── Roads API snapToRoads ─────────────────────────────────────────────────
    const snapped = [];
    let   roadsOk = false;

    for (let b = 0; b < batches.length; b++) {
      if (b > 0) await new Promise(r => setTimeout(r, 150));
      const { slice: batch, turnCut } = batches[b];
      const path = batch.map(p => `${parseFloat(p.lat).toFixed(7)},${parseFloat(p.lng).toFixed(7)}`).join('|');

      try {
        const res  = await fetch(
          `https://roads.googleapis.com/v1/snapToRoads?path=${path}&interpolate=true&key=${GOOGLE_MAPS_API_KEY}`
        );
        const data = await res.json();

        if (data.error) { console.warn('Roads API error:', data.error.message); break; }

        if (data.snappedPoints?.length > 0) {
          roadsOk = true;
          if (b === 0) {
            snapped.push(...data.snappedPoints.map(p => [p.location.latitude, p.location.longitude]));
          } else {
            // Turn cut → 1 shared anchor point → skip snapped output for originalIndex 0
            // Size cut → SZ_OVL shared points → skip snapped output for originalIndex < SZ_OVL
            const skipTo   = turnCut ? 1 : SZ_OVL;
            const firstNew = data.snappedPoints.findIndex(
              p => p.originalIndex !== undefined && p.originalIndex >= skipTo
            );
            const toAdd = firstNew >= 0 ? data.snappedPoints.slice(firstNew) : data.snappedPoints;
            snapped.push(...toAdd.map(p => [p.location.latitude, p.location.longitude]));
          }
        }
      } catch (err) {
        console.warn('Roads API batch failed:', err);
        break;
      }
    }

    if (roadsOk && snapped.length > pts.length) {
      console.log(`Roads API snapToRoads: ${pts.length} pts, ${batches.length} batches → ${snapped.length} snapped`);
      return snapped;
    }

    // ── Fallback: DirectionsService in short segments ─────────────────────────
    console.warn('Roads API unavailable — falling back to DirectionsService segments');

    const resample = (arr, n) => {
      if (arr.length <= n) return arr;
      const out = [], step = (arr.length - 1) / (n - 1);
      for (let j = 0; j < n; j++) out.push(arr[Math.min(Math.round(j * step), arr.length - 1)]);
      out[out.length - 1] = arr[arr.length - 1];
      return out;
    };

    const routeSegment = (segPts) => new Promise(resolve => {
      if (!window.google?.maps?.DirectionsService) {
        resolve({ path: segPts.map(p => [p.lat, p.lng]), ok: false }); return;
      }
      const sel  = resample(segPts, 25);
      const orig = new window.google.maps.LatLng(sel[0].lat, sel[0].lng);
      const dest = new window.google.maps.LatLng(sel[sel.length - 1].lat, sel[sel.length - 1].lng);
      const wpts = sel.slice(1, -1).map(p => ({ location: new window.google.maps.LatLng(p.lat, p.lng), stopover: false }));
      new window.google.maps.DirectionsService().route(
        { origin: orig, destination: dest, waypoints: wpts, travelMode: window.google.maps.TravelMode.DRIVING, optimizeWaypoints: false },
        (result, status) => {
          if (status === window.google.maps.DirectionsStatus.OK && result.routes[0]) {
            const p = [];
            result.routes[0].legs.forEach(leg => leg.steps.forEach(step => step.path.forEach(pt => p.push([pt.lat(), pt.lng()]))));
            resolve({ path: p, ok: true });
          } else {
            resolve({ path: segPts.map(p => [p.lat, p.lng]), ok: false });
          }
        }
      );
    });

    const NUM_SEGS = pts.length > 150 ? 6 : pts.length > 75 ? 3 : 1;
    const SEG_SIZE = Math.ceil(pts.length / NUM_SEGS);
    const fullPath = [];
    let dsOk = false;

    for (let s = 0; s < NUM_SEGS; s++) {
      if (s > 0) await new Promise(r => setTimeout(r, 200));
      const start = s * SEG_SIZE;
      const end   = Math.min(pts.length, start + SEG_SIZE + 1);
      const { path, ok } = await routeSegment(pts.slice(start, end));
      if (ok) dsOk = true;
      fullPath.push(...(s === 0 ? path : path.slice(1)));
    }

    return dsOk && fullPath.length > pts.length ? fullPath : pts.map(p => [p.lat, p.lng]);
  };

  // ── Statistics ────────────────────────────────────────────────────────────

  const calculateStatistics = (trackData) => {
    if (trackData.length < 2) return;
    let totalDistance = 0; let totalSpeed = 0; let maxSpeed = 0; let validSpeedPoints = 0;
    for (let i = 1; i < trackData.length; i++) {
      totalDistance += calculateDistance(
        parseFloat(trackData[i - 1].latitude), parseFloat(trackData[i - 1].longitude),
        parseFloat(trackData[i].latitude),     parseFloat(trackData[i].longitude)
      );
      if (trackData[i].speed) {
        const speed = parseFloat(trackData[i].speed);
        totalSpeed += speed; validSpeedPoints++;
        if (speed > maxSpeed) maxSpeed = speed;
      }
    }
    console.log("avgSpeed", validSpeedPoints > 0 ? totalSpeed / validSpeedPoints : 0);
  };

  // ── vehicleData ───────────────────────────────────────────────────────────

  const vehicleData = async (vehicleNumber, startDate, endDate) => {
    setIsLoadingData(true); setIsLoadingPolylines(true); setApiError("");
    setIdleCalculationPoints([]);
    setIdlePeriods([]);

    try {
      const payload  = { vehicleNumber, startTime: formatDateTime(startDate), endTime: formatDateTime(endDate) };
      const respData = await postDataWithBody(fetchVehicleTrackingWithIdealPoints, payload, {});
      console.log("Raw GPS data received:", respData.raw_data?.length, "points");

      const validatedData = validateAndFilterGPSPoints(respData.raw_data || [], {
        maxSpeed: 150, maxDistanceJump: 2, outlierThreshold: 2.5,
      });
      console.log(`GPS validation complete: ${validatedData.length} points`);

      setVehicleTrackingData(validatedData);
      setIdleCalculationPoints(respData.idle_events || []);
      calculateStatistics(validatedData);

      if (validatedData.length > 1) {
        setIsLoadingMap(true);
        const coordinates = validatedData.map((point, index) => ({
          lat:           parseFloat(point.latitude),
          lng:           parseFloat(point.longitude),
          originalIndex: index,
          timestamp:     point.vehicle_tracking_time,
          speed:         parseFloat(point.speed) || 0,
        }));

        if (coordinates.length >= 2) {
          toast.info("Routing along roads...", { autoClose: 3000 });
          const routedPath = await snapPointsToRoad(coordinates);
          const isRoaded   = routedPath.length > coordinates.length;
          setRouteCoordinates(routedPath);
          setRoadSnappingEnabled(isRoaded);
          if (isRoaded) {
            toast.success(`Road route loaded — ${validatedData.length} GPS points`, { autoClose: 2000 });
          } else {
            toast.warning("Using GPS track (Directions API unavailable)", { autoClose: 3000 });
          }
        }
        setIsLoadingMap(false);
      }
    } catch (err) {
      console.error("Failed to execute query:", err);
      toast.error("Failed to execute query");
      setVehicleTrackingData([]); setRouteCoordinates([]); setRoadSnappingEnabled(false);
    } finally {
      setIsLoadingData(false); setIsLoadingPolylines(false); setIsLoadingMap(false);
    }
  };

  useEffect(() => {
    if (data) vehicleData(data.vehicleNumber, data.startTime, data.endTime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const getInitialZoom = () => (vehicleTrackingData.length > 0 ? 12 : 7);

  // ── Idle statistics panel ─────────────────────────────────────────────────

  const renderEnhancedIdleStatistics = () => (
    <div className="col-md-12 mt-3">
      <div style={{ background: "white", borderRadius: 12, overflow: "hidden", boxShadow: "0 2px 10px rgba(0,0,0,0.07)", border: "1px solid #e2e8f0" }}>
        {/* Header */}
        <div style={{ background: "linear-gradient(135deg, #0f4a45 0%, #1a7a73 100%)", padding: "10px 18px", display: "flex", alignItems: "center", gap: 10 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
          </svg>
          <span style={{ color: "white", fontWeight: 700, fontSize: 14, letterSpacing: "0.4px" }}>Idle Time Analysis</span>
        </div>

        {/* Stat tiles */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, padding: "14px 16px 10px" }}>
          <div style={{ background: "linear-gradient(135deg,#fff7ed,#ffedd5)", border: "1px solid #fed7aa", borderLeft: "3px solid #ff8c00", borderRadius: 8, padding: "10px 14px", textAlign: "center" }}>
            <div style={{ fontSize: 11, color: "#92400e", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Total Idle Time</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#ff8c00" }}>{totalIdleTime.formattedTotal}</div>
          </div>
          <div style={{ background: "linear-gradient(135deg,#fff1f2,#ffe4e6)", border: "1px solid #fecdd3", borderLeft: "3px solid #dc3545", borderRadius: 8, padding: "10px 14px", textAlign: "center" }}>
            <div style={{ fontSize: 11, color: "#881337", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Idle Locations</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#dc3545" }}>{totalIdleTime.idleCount}</div>
          </div>
          <div style={{ background: "linear-gradient(135deg,#f5f3ff,#ede9fe)", border: "1px solid #ddd6fe", borderLeft: "3px solid #6610f2", borderRadius: 8, padding: "10px 14px", textAlign: "center" }}>
            <div style={{ fontSize: 11, color: "#4c1d95", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Avg Idle Duration</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#6610f2" }}>
              {totalIdleTime.idleCount > 0
                ? formatDuration(totalIdleTime.totalMinutes / totalIdleTime.idleCount)
                : "0 minutes"}
            </div>
          </div>
        </div>

        {/* Idle location list */}
        {idlePeriods.length > 0 && (
          <div style={{ padding: "0 16px 14px" }}>
            <div style={{ fontSize: 12, color: "#0f4a45", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 10, paddingTop: 4, borderTop: "1px solid #e2e8f0" }}>
              Idle Locations
            </div>
            <div style={{ maxHeight: 260, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8, paddingRight: 4 }}>
              {idlePeriods.map((period, index) => (
                <div key={index} style={{
                  display: "flex", alignItems: "center", gap: 12,
                  background: index % 2 === 0 ? "#f4f8fd" : "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderLeft: `3px solid ${index === 0 ? "#0f4a45" : "#94a3b8"}`,
                  borderRadius: 8, padding: "10px 14px",
                }}>
                  <div style={{
                    width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
                    background: index === 0 ? "linear-gradient(135deg,#0f4a45,#1a7a73)" : "linear-gradient(135deg,#94a3b8,#64748b)",
                    color: "white", display: "flex", alignItems: "center",
                    justifyContent: "center", fontWeight: 700, fontSize: 13,
                  }}>{index + 1}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "auto 1fr 1fr", gap: "0 20px", flex: 1, alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: 10, color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px" }}>Duration</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#ff6600" }}>{formatDuration(period.duration)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px" }}>Start Time</div>
                      <div style={{ fontSize: 12, color: "#334155" }}>{formatTrackingTime(period.startTime)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px" }}>End Time</div>
                      <div style={{ fontSize: 12, color: "#334155" }}>{formatTrackingTime(period.endTime)}</div>
                    </div>
                  </div>
                  {period.duration > 30 && (
                    <span style={{ fontSize: 10, fontWeight: 700, color: "#dc2626", background: "#fee2e2", borderRadius: 20, padding: "2px 8px", flexShrink: 0 }}>Long</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <div className="row" style={{ margin: 0, alignItems: "stretch" }}>
        {/* ── Left Panel ── */}
        <div className="col-lg-4" style={{ display: "flex", flexDirection: "column", padding: "14px 8px 14px 0" }}>
      <div style={{ flex: 1, background: "white", borderRadius: 12, padding: "16px", boxShadow: "0 2px 10px rgba(0,0,0,0.07)", borderLeft: "4px solid #0f4a45", display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>

          {/* Vehicle Number */}
          <div
            style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", background: "linear-gradient(135deg,#ffffff,#f0f6fb)", border: "1px solid #d0e4f0", borderLeft: "3px solid #1a7a73", borderRadius: 10, transition: "all 0.25s ease", cursor: "default", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(26,122,115,0.13)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)";    e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.05)"; }}
          >
            <div style={{ width: 44, height: 44, borderRadius: 10, background: "linear-gradient(135deg,#ccfbf1,#99f6e4)", color: "#1a7a73", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
              </svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, color: "#0f4a45", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 3 }}>Vehicle Number</div>
              <div style={{ fontSize: 15, color: "#0f172a", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{data?.vehicleNumber}</div>
            </div>
          </div>

          {/* Start Date & Time */}
          <div
            style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", background: "linear-gradient(135deg,#ffffff,#f0fbf5)", border: "1px solid #c6e8d5", borderLeft: "3px solid #059669", borderRadius: 10, transition: "all 0.25s ease", cursor: "default", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(5,150,105,0.12)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)";    e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.05)"; }}
          >
            <div style={{ width: 44, height: 44, borderRadius: 10, background: "linear-gradient(135deg,#d1fae5,#a7f3d0)", color: "#059669", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, color: "#0f4a45", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 3 }}>Start Date &amp; Time</div>
              <div style={{ fontSize: 15, color: "#0f172a", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{data ? formatTrackingTime(data.startTime) : ""}</div>
            </div>
          </div>

          {/* End Date & Time */}
          <div
            style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", background: "linear-gradient(135deg,#ffffff,#fdf0f7)", border: "1px solid #f0c8e0", borderLeft: "3px solid #db2777", borderRadius: 10, transition: "all 0.25s ease", cursor: "default", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(219,39,119,0.10)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)";    e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.05)"; }}
          >
            <div style={{ width: 44, height: 44, borderRadius: 10, background: "linear-gradient(135deg,#fce7f3,#fbcfe8)", color: "#db2777", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, color: "#0f4a45", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 3 }}>End Date &amp; Time</div>
              <div style={{ fontSize: 15, color: "#0f172a", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{data ? formatTrackingTime(data.endTime) : ""}</div>
            </div>
          </div>

        </div>

        {apiError && (
          <div className="mt-3 alert alert-danger">
            <strong>API Error: </strong> {apiError}
          </div>
        )}

        <div style={{ borderTop: "1px solid #e2e8f0", margin: "4px 0" }} />

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {vehicleTrackingData.length > 1 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <button
                onClick={handlePlayPause}
                style={{
                  display: "flex", alignItems: "center", gap: 7,
                  padding: "6px 16px", borderRadius: 8, border: "none", cursor: "pointer",
                  background: animPlaying
                    ? "linear-gradient(135deg,#dc3545,#b02a37)"
                    : "linear-gradient(135deg,#28a745,#1e7e34)",
                  color: "#fff", fontWeight: 700, fontSize: 12,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.18)", transition: "background 0.2s",
                }}
              >
                {animPlaying ? (
                  <>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="white"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                    Pause
                  </>
                ) : (
                  <>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="white"><polygon points="5,3 19,12 5,21"/></svg>
                    Play Route
                  </>
                )}
              </button>
              {/* Speed controls */}
              <div style={{ display: "flex", alignItems: "center", gap: 3, background: "#f1f5f9", borderRadius: 8, padding: "3px 5px", border: "1px solid #e2e8f0" }}>
                {[1, 2, 3, 4].map(s => (
                  <button
                    key={s}
                    onClick={() => setAnimSpeed(s)}
                    style={{
                      padding: "3px 8px", borderRadius: 6, border: "none", cursor: "pointer",
                      fontSize: 11, fontWeight: 700,
                      background: animSpeed === s ? "linear-gradient(135deg,#0f4a45,#1a7a73)" : "transparent",
                      color: animSpeed === s ? "#fff" : "#64748b",
                      transition: "all 0.15s",
                    }}
                  >{s}x</button>
                ))}
              </div>
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#0f4a45", background: "#e8f3fa", borderRadius: 20, padding: "3px 12px", border: "1px solid #b3d4ea" }}>
              {vehicleTrackingData.length} GPS points
            </span>
            {vehicleTrackingData.length > 0 && (
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: "#0f172a", cursor: "pointer", userSelect: "none" }}>
                <input
                  type="checkbox"
                  checked={showDataPoints}
                  onChange={(e) => {
                    setShowDataPoints(e.target.checked);
                    if (!e.target.checked) setActiveDataPointPopup(null);
                  }}
                  style={{ width: 15, height: 15, accentColor: "#0f4a45", cursor: "pointer" }}
                />
                Show GPS points
              </label>
            )}
          </div>
        </div>
      </div>
        </div>

        {/* ── Right Panel ── */}
        <div className="col-lg-8" style={{ display: "flex", flexDirection: "column", padding: "14px 0 14px 8px" }}>
          <div style={{ flex: 1, borderRadius: 12, overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,0.09)", display: "flex", flexDirection: "column" }}>
            <div style={{ background: "linear-gradient(to right, #0f3d3a 0%, #0f4a45 50%, #1a7a73 100%)", padding: "5px 14px", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              <div style={{ width: 26, height: 26, borderRadius: 6, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/>
                  <line x1="9" y1="3" x2="9" y2="18"/>
                  <line x1="15" y1="6" x2="15" y2="21"/>
                </svg>
              </div>
              <span style={{ color: "#fff", fontWeight: 700, fontSize: 13, flex: 1 }}>GPS Track Map</span>
              <span style={{ background: "rgba(255,255,255,0.18)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 20, padding: "2px 9px", fontSize: 10, fontWeight: 600, flexShrink: 0 }}>
                {(MAP_TYPE_OPTIONS.find(o => o.id === mapType)?.label ?? mapType)} layer
              </span>
              <button
                onClick={toggleFullscreen}
                title={isFullscreen ? "Exit Full Map View" : "Full Map View"}
                style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 6, padding: "4px 10px", color: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 600, display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  {isFullscreen
                    ? <path d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3"/>
                    : <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"/>
                  }
                </svg>
                {isFullscreen ? "Exit Full View" : "Full Map View"}
              </button>
            </div>

            {/* Search bar */}
            <div style={{ padding: "8px 12px", background: "#fff", borderBottom: "1px solid #e2e8f0", position: "relative" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 9, padding: "6px 10px" }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  placeholder="Search city, area, landmark or coordinates…"
                  style={{ flex: 1, border: "none", outline: "none", fontSize: 13, color: "#0f172a", background: "transparent" }}
                />
                {isSearching && (
                  <div style={{ width: 14, height: 14, border: "2px solid #e2e8f0", borderTop: "2px solid #0f4a45", borderRadius: "50%", animation: "spin 0.7s linear infinite", flexShrink: 0 }} />
                )}
                {searchQuery && !isSearching && (
                  <button onClick={clearSearch} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: "#94a3b8", display: "flex", alignItems: "center" }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                )}
              </div>
              {searchResults.length > 0 && (
                <div style={{ position: "absolute", top: "100%", left: 12, right: 12, background: "#fff", border: "1px solid #e2e8f0", borderRadius: "0 0 10px 10px", boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 1050, maxHeight: 260, overflowY: "auto" }}>
                  {searchResults.map((result) => (
                    <div
                      key={result.place_id}
                      onClick={() => selectSearchResult(result)}
                      style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 14px", cursor: "pointer", borderBottom: "1px solid #f1f5f9", transition: "background 0.15s" }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "#f8fafc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="#ef4444" stroke="none" style={{ flexShrink: 0, marginTop: 2 }}>
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                      </svg>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{result.structured_formatting?.main_text || result.description}</div>
                        {result.structured_formatting?.secondary_text && (
                          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>{result.structured_formatting.secondary_text}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

      <div
        ref={mapContainerRef}
        style={{
          flex: isFullscreen ? "none" : 1,
          height: isFullscreen ? "100vh" : "auto",
          minHeight: isFullscreen ? undefined : 520,
          position: "relative", overflow: "hidden",
          isolation: "isolate",
          background: "white",
        }}
      >
        {(isLoadingData || isLoadingPolylines || isLoadingMap) && (
          <LoadingOverlay
            message={
              isLoadingData      ? "Fetching GPS data..."  :
              isLoadingPolylines ? "Processing route..."   :
              isLoadingMap       ? "Rendering map..."      : "Loading..."
            }
          />
        )}

        {!isLoaded ? (
          <LoadingOverlay message="Loading Google Maps..." />
        ) : loadError ? (
          <div className="alert alert-danger m-3">Failed to load Google Maps.</div>
        ) : (
          <div style={{ position: "relative", height: "100%", width: "100%" }}>
            <GoogleMap
              mapContainerStyle={MAP_CONTAINER_STYLE}
              center={DEFAULT_CENTER}
              zoom={getInitialZoom()}
              mapTypeId={mapType}
              onLoad={onMapLoad}
              options={{
                mapTypeControl:    false,
                streetViewControl: true,
                fullscreenControl: true,
                zoomControl:       true,
                clickableIcons:    false,
                gestureHandling:   "greedy",
              }}
            >
              {!isLoadingPolylines && !isLoadingMap && (
                <>
                  {/* Route polyline */}
                  {routeCoordinates.length > 1 && (
                    <Polyline
                      path={routeCoordinates.map(([lat, lng]) => ({ lat, lng }))}
                      options={{
                        strokeColor:   roadSnappingEnabled ? "#ff4444" : "#4444ff",
                        strokeWeight:  roadSnappingEnabled ? 6 : 4,
                        strokeOpacity: 0.8,
                      }}
                    />
                  )}

                  {/* Start marker */}
                  {routeCoordinates.length > 0 && (
                    <>
                      <Marker
                        position={{ lat: routeCoordinates[0][0], lng: routeCoordinates[0][1] }}
                        icon={buildPinIcon("green")}
                        zIndex={900}
                        onClick={() => setActiveEndpointPopup(activeEndpointPopup === "start" ? null : "start")}
                      />
                      {activeEndpointPopup === "start" && (
                        <OverlayView
                          position={{ lat: routeCoordinates[0][0], lng: routeCoordinates[0][1] }}
                          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                        >
                          <EndpointPopupContent
                            type="start"
                            vehicleTime={vehicleTrackingData[0]?.vehicle_tracking_time}
                            address={startAddress}
                            coords={routeCoordinates[0]}
                            onClose={() => setActiveEndpointPopup(null)}
                            formatTrackingTime={formatTrackingTime}
                          />
                        </OverlayView>
                      )}
                    </>
                  )}

                  {/* End marker */}
                  {routeCoordinates.length > 1 && (
                    <>
                      <Marker
                        position={{
                          lat: routeCoordinates[routeCoordinates.length - 1][0],
                          lng: routeCoordinates[routeCoordinates.length - 1][1],
                        }}
                        icon={buildPinIcon("red")}
                        zIndex={900}
                        onClick={() => setActiveEndpointPopup(activeEndpointPopup === "end" ? null : "end")}
                      />
                      {activeEndpointPopup === "end" && (
                        <OverlayView
                          position={{
                            lat: routeCoordinates[routeCoordinates.length - 1][0],
                            lng: routeCoordinates[routeCoordinates.length - 1][1],
                          }}
                          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                        >
                          <EndpointPopupContent
                            type="end"
                            vehicleTime={vehicleTrackingData[vehicleTrackingData.length - 1]?.vehicle_tracking_time}
                            address={endAddress}
                            coords={routeCoordinates[routeCoordinates.length - 1]}
                            onClose={() => setActiveEndpointPopup(null)}
                            formatTrackingTime={formatTrackingTime}
                          />
                        </OverlayView>
                      )}
                    </>
                  )}

                  {/* GPS data-point markers (toggle: Show GPS points) */}
                  {showDataPoints && vehicleTrackingData.map((point, index) => {
                    const lat = parseFloat(point.latitude);
                    const lng = parseFloat(point.longitude);
                    if (isNaN(lat) || isNaN(lng)) return null;
                    return (
                      <Fragment key={`pt-${index}`}>
                        <Marker
                          position={{ lat, lng }}
                          icon={buildDotIcon()}
                          zIndex={700}
                          onClick={() => setActiveDataPointPopup(activeDataPointPopup === index ? null : index)}
                        />
                        {activeDataPointPopup === index && (
                          <OverlayView position={{ lat, lng }} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                            <DataPointPopupContent
                              point={point}
                              index={index}
                              address={datapointAddr.index === index ? datapointAddr.address : null}
                              onClose={() => setActiveDataPointPopup(null)}
                              formatTrackingTime={formatTrackingTime}
                            />
                          </OverlayView>
                        )}
                      </Fragment>
                    );
                  })}

                  {/* Idle markers */}
                  {idlePeriods.map((idlePeriod, index) => (
                    <div key={`idle-${index}`}>
                      <Marker
                        position={{ lat: idlePeriod.location.lat, lng: idlePeriod.location.lng }}
                        icon={buildIdleIcon(idlePeriod, index)}
                        zIndex={800}
                        onClick={() => setActiveIdlePopup(activeIdlePopup === index ? null : index)}
                      />
                      {activeIdlePopup === index && (
                        <OverlayView
                          position={{ lat: idlePeriod.location.lat, lng: idlePeriod.location.lng }}
                          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                        >
                          <IdlePopupContent
                            idlePeriod={idlePeriod}
                            index={index}
                            onClose={() => setActiveIdlePopup(null)}
                            formatDuration={formatDuration}
                            formatTrackingTime={formatTrackingTime}
                          />
                        </OverlayView>
                      )}
                    </div>
                  ))}

                  {/* Animated vehicle marker */}
                  {animPos && isLoaded && (
                    <Marker
                      position={animPos}
                      icon={getTrackVehicleIcon(_vehicleType, animHeading)}
                      zIndex={2000}
                      onClick={() => setShowVehiclePopup(v => !v)}
                    >
                      {showVehiclePopup && (() => {
                        const fmtDt = (s) => {
                          if (!s) return null;
                          try {
                            const d = new Date(s);
                            if (isNaN(d)) return s;
                            const pad = n => String(n).padStart(2, '0');
                            const h = d.getHours(), ampm = h >= 12 ? 'PM' : 'AM';
                            return `${pad(d.getDate())}-${pad(d.getMonth()+1)}-${d.getFullYear()} ${pad(h%12||12)}:${pad(d.getMinutes())} ${ampm}`;
                          } catch { return s; }
                        };
                        const Row = ({ label, value, accent }) => (
                          <div style={{ display: "grid", gridTemplateColumns: "90px 1fr", gap: 6, padding: "5px 0", borderBottom: "1px solid #f1f5f9" }}>
                            <span style={{ color: "#94a3b8", fontSize: 11, fontWeight: 600 }}>{label}</span>
                            <span style={{ color: accent || "#1e293b", fontSize: 12, fontWeight: accent ? 700 : 400, textAlign: "right", wordBreak: "break-word" }}>{value}</span>
                          </div>
                        );
                        const SectionHead = ({ children }) => (
                          <div style={{ fontSize: 10, fontWeight: 700, color: "#fff", background: "linear-gradient(90deg,#0f4a45,#1a7a73)", padding: "3px 10px", borderRadius: 4, margin: "10px -12px 6px", textTransform: "uppercase", letterSpacing: "0.6px" }}>{children}</div>
                        );
                        const emoji = TRACK_VEHICLE_EMOJI[(_vehicleType || '').toLowerCase().replace(/[\s_-]+/g, '')] || '🚘';
                        return (
                          <InfoWindow onCloseClick={() => setShowVehiclePopup(false)}>
                            <div style={{ width: 258, borderRadius: 12, overflow: "hidden", fontFamily: "'Segoe UI',sans-serif", fontSize: 12 }}>
                              <div style={{ background: "linear-gradient(135deg,#0f4a45,#1a7a73)", color: "#fff", padding: "10px 12px", display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ fontSize: 22, lineHeight: 1, flexShrink: 0 }}>{emoji}</span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontWeight: 800, fontSize: 14, letterSpacing: "0.3px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{data?.vehicleNumber || vehicleInfo?.vehicleNumber || "Vehicle"}</div>
                                  {(_vehicleType || vehicleInfo?.vehicleType) && <div style={{ fontSize: 11, opacity: 0.85, marginTop: 1 }}>{_vehicleType || vehicleInfo?.vehicleType}</div>}
                                </div>
                                <button
                                  onClick={() => setShowVehiclePopup(false)}
                                  style={{ background: "rgba(255,255,255,0.22)", border: "none", borderRadius: "50%", width: 24, height: 24, cursor: "pointer", color: "#fff", fontSize: 14, fontWeight: 700, lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                                >✕</button>
                              </div>
                              <div style={{ padding: "10px 12px 12px", background: "#fff" }}>
                                {vehicleInfo && [
                                  ["Brand",     vehicleInfo.brand    || null],
                                  ["Model",     vehicleInfo.model    || null],
                                  ["Fuel Type", vehicleInfo.fuelType || null],
                                  ["Reg. No.",  vehicleInfo.registrationNumber || null],
                                ].some(([, v]) => v) && (
                                  <>
                                    <SectionHead>Vehicle</SectionHead>
                                    {[
                                      ["Brand",     vehicleInfo.brand    || null],
                                      ["Model",     vehicleInfo.model    || null],
                                      ["Fuel Type", vehicleInfo.fuelType || null],
                                      ["Reg. No.",  vehicleInfo.registrationNumber || null],
                                    ].filter(([, v]) => v).map(([l, v]) => <Row key={l} label={l} value={v} />)}
                                  </>
                                )}
                                <SectionHead>Trip</SectionHead>
                                {[
                                  ["Vehicle",    data?.vehicleNumber || null],
                                  ["Start Time", fmtDt(data?.startTime)],
                                  ["End Time",   fmtDt(data?.endTime)],
                                ].filter(([, v]) => v).map(([l, v]) => <Row key={l} label={l} value={v} />)}
                                {vehicleInfo && (vehicleInfo.driverName || vehicleInfo.driverPhone) && (
                                  <>
                                    <SectionHead>Driver</SectionHead>
                                    {[
                                      ["Name",    vehicleInfo.driverName    || null],
                                      ["Phone",   vehicleInfo.driverPhone   || null],
                                      ["License", vehicleInfo.driverLicense || null],
                                      ["Expiry",  vehicleInfo.licenseExpiry || null],
                                    ].filter(([, v]) => v).map(([l, v]) => <Row key={l} label={l} value={v} />)}
                                  </>
                                )}
                              </div>
                            </div>
                          </InfoWindow>
                        );
                      })()}
                    </Marker>
                  )}
                </>
              )}
              {showTransit && <TransitLayer />}
            </GoogleMap>

            {!isLoadingData && !isLoadingPolylines && (
              <CustomLayerControl mapType={mapType} setMapType={setMapType} />
            )}
            <button
              onClick={() => setShowTransit(p => !p)}
              title={showTransit ? "Hide transit" : "Show transit"}
              style={{
                position: "absolute", top: 60, right: 10, zIndex: 1000,
                width: 40, height: 40, borderRadius: 8, border: "none", cursor: "pointer",
                background: showTransit ? "#1a7a73" : "white",
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                stroke={showTransit ? "#fff" : "#555"} strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="2" width="16" height="16" rx="2"/>
                <path d="M4 10h16"/><path d="M8 2v8"/><path d="M16 2v8"/>
                <path d="M8 18l-2 4"/><path d="M16 18l2 4"/>
              </svg>
            </button>
          </div>
        )}
      </div>
          </div>
        </div>
      </div>

      {/* Idle statistics panel */}
      {vehicleTrackingData.length > 0 && idlePeriods.length > 0 && (
        <div className="container-fluid" style={{ padding: "0 0 16px" }}>
          <div className="row">{renderEnhancedIdleStatistics()}</div>
        </div>
      )}

      <style>{`
        @keyframes fadeInPanel {
          from { opacity: 0; transform: translate(-50%, calc(-100% - 10px)); }
          to   { opacity: 1; transform: translate(-50%, calc(-100% - 20px)); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        :fullscreen > div { height: 100vh !important; }
        :-webkit-full-screen > div { height: 100vh !important; }
        .gm-ui-hover-effect { display: none !important; }
        .gm-style-iw-chr { display: none !important; }
        .gm-style-iw-d { overflow: hidden !important; padding: 0 !important; }
        .gm-style-iw-c { padding: 0 !important; border-radius: 12px !important; box-shadow: 0 4px 16px rgba(0,0,0,0.18) !important; }
      `}</style>
    </>
  );
};

export default GpsTrackViewDetails;